import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { isCouponSendDue } from '@service/giftStore/page/giftCouponRedeem/giftCouponRedeem.utils';
import {
  giftCouponStatisticsDataAtom,
  redemptionDashboardDataAtom,
  redemptionDashboardStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  DeliverCouponDto,
  ExtraPaymentDto,
  UpdateDeliveredCouponFieldsDto,
  cancelSpawnedCoupon,
  deliverCoupons,
  fulfillFromExisting,
  getDonorCandidates,
  getPendingChargeEstimate,
  payExtraPayment,
  rejectCoupon,
  updateDeliveredCouponFields,
  updateRecipientPhone,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  giftCouponStatisticsUrl,
  redemptionDashboardUrl,
  useGiftCouponStatisticsHook,
  useRedemptionDashboardHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import { getChannelBalances } from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import { ChannelBalanceSummary } from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import {
  DeliveryChannel,
  GiftCouponForRedemption,
  GiftCouponForRedemptionDashboard,
  PendingChargeEstimateResponse,
  PhoneHistoryItem,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useQueryClient } from 'react-query';

import {
  computeSelectedChargeEstimate,
  couponEstimateKey,
} from '../utils/chargeEstimate.utils';
import {
  getBrandName,
  getCouponSaleRetail,
  isOverIssued,
} from '../utils/redemption.utils';
import { useToast } from './useToast';

export interface DeliverModalState {
  couponIds: string[];
  priceRetail: number;
  priceWhole: string;
  priceRebate: string;
  /** 빈 문자열이면 API에 deliveryChannel 미전송 */
  deliveryChannel: DeliveryChannel | '';
  /** 도매가×건수 총액만큼 해당 매체에서 실제 발송했음을 확인했는지 */
  amountConfirmed: boolean;
  isBulk: boolean;
}

export interface HistoryModalState {
  phone: string;
  history: PhoneHistoryItem[];
}

export interface ShareHistoryModalState {
  fullCode: string;
  history: Array<{ sharedAt: Date; unsharedAt?: Date }>;
}

export function useRedemptionPage() {
  useRedemptionDashboardHook();
  useGiftCouponStatisticsHook();

  const queryClient = useQueryClient();
  const dashboardStatus = useRecoilValue(redemptionDashboardStatusAtom);
  const dashboard = useRecoilValue(redemptionDashboardDataAtom);
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  const allRequested = useMemo(() => {
    const list = dashboard?.requested ?? [];
    return [...list].sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });
  }, [dashboard]);

  // 추가결제 대기: 추가교환 spawn 쿠폰 중 아직 정산되지 않은 건.
  // 정산 = 추가결제 완료(extraPayment.isPaid / settledBy='extraPayment') 또는
  //        대체발송으로 재고 충당(settledBy='inventory'). 둘 중 하나면 발송대기로 이동.
  // (백엔드 발송가드와 동일 기준 — inventory 정산 건은 통과)
  const isExtraPaymentPending = (c: GiftCouponForRedemptionDashboard) =>
    c.isExtraPaid === true &&
    c.settledBy !== 'inventory' &&
    c.settledBy !== 'extraPayment' &&
    !c.extraPayment?.isPaid;

  const extraPaymentPending = useMemo(
    () => allRequested.filter(isExtraPaymentPending),
    [allRequested],
  );

  // 발송대기(일반): 일반 Requested + 추가결제 완료된 스폰 쿠폰.
  const visibleRequested = useMemo(
    () => allRequested.filter((c) => !isExtraPaymentPending(c)),
    [allRequested],
  );

  const todayDelivered = useMemo(() => {
    const list = dashboard?.todayDelivered ?? [];
    return [...list].sort((a, b) => {
      const deliveredA = a.gifticon?.deliveredAt
        ? new Date(a.gifticon.deliveredAt).getTime()
        : 0;
      const deliveredB = b.gifticon?.deliveredAt
        ? new Date(b.gifticon.deliveredAt).getTime()
        : 0;
      if (deliveredA !== deliveredB) return deliveredB - deliveredA;
      const requestedA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const requestedB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return requestedA - requestedB;
    });
  }, [dashboard]);

  // 매체별 충전 필요액 예측 — 구서버(엔드포인트 미배포)면 404 → null 유지, 영역 미노출
  const [chargeEstimate, setChargeEstimate] =
    useState<PendingChargeEstimateResponse | null>(null);
  const estimateUnsupportedRef = useRef(false);

  const refreshChargeEstimate = useCallback(async () => {
    if (estimateUnsupportedRef.current) return;
    try {
      setChargeEstimate(await getPendingChargeEstimate());
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        estimateUnsupportedRef.current = true; // 미배포 — 재시도 무의미
      }
      setChargeEstimate(null); // 예측은 보조 지표 — 실패해도 화면만 숨기고 조용히 넘어간다
    }
  }, []);

  useEffect(() => {
    refreshChargeEstimate();
  }, [refreshChargeEstimate]);

  // 채널 충전금 미러 잔액 — 예상 차감액과 나란히 보여 부족분을 미리 알린다.
  // 구서버(원장 미배포)면 404 → null 유지, 잔액 표기만 숨김
  const [channelBalances, setChannelBalances] = useState<ChannelBalanceSummary[] | null>(
    null,
  );
  const balancesUnsupportedRef = useRef(false);

  const refreshChannelBalances = useCallback(async () => {
    if (balancesUnsupportedRef.current) return;
    try {
      setChannelBalances(await getChannelBalances());
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        balancesUnsupportedRef.current = true; // 미배포 — 재시도 무의미
      }
      setChannelBalances(null);
    }
  }, []);

  useEffect(() => {
    refreshChannelBalances();
  }, [refreshChannelBalances]);

  const invalidateDashboard = useCallback(() => {
    queryClient.invalidateQueries(redemptionDashboardUrl);
    queryClient.invalidateQueries(giftCouponStatisticsUrl);
    refreshChargeEstimate(); // 발송/정산 후 대기 구성이 바뀌므로 예상액도 갱신
    refreshChannelBalances(); // 발송 차감이 자동 기장되므로 미러 잔액도 갱신
  }, [queryClient, refreshChargeEstimate, refreshChannelBalances]);

  // Filters & selection
  const [productFilter, setProductFilterRaw] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showTodayDelivered, setShowTodayDelivered] = useState(false);

  // Modals
  const [deliverModal, setDeliverModal] = useState<DeliverModalState | null>(null);
  const [historyModal, setHistoryModal] = useState<HistoryModalState | null>(null);
  const [shareHistoryModal, setShareHistoryModal] =
    useState<ShareHistoryModalState | null>(null);

  // Toast — 공용 훅 사용 (자동 소멸 1200ms). 반환 키는 기존대로 toastMessage/showToast 유지.
  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(1200);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 상품 필터 변경 시 잘못된 일괄처리 방지를 위해 선택 초기화
  const setProductFilter = useCallback((next: string) => {
    setProductFilterRaw((prev) => {
      if (prev !== next) {
        setSelectedIds(new Set());
      }
      return next;
    });
  }, []);

  // 발송 슬롯(10/14/18시) 경계가 지나면 "지금 발송" 대상 수가 바뀌므로
  // 데이터 갱신이 없어도 1분마다 재계산되도록 틱을 둔다.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // 상품키별 예측 단가 — 상품별 대기 목록의 예상 금액 계산에 재사용 (null=예측 미지원)
  const estimateUnitPriceByKey = useMemo(() => {
    if (!chargeEstimate) return null;
    const map = new Map<string, number>();
    for (const bucket of chargeEstimate.channels) {
      for (const item of bucket.items) {
        map.set(item.key, item.unitPriceWhole);
      }
    }
    return map;
  }, [chargeEstimate]);

  // Product filter options (with duplicate count, oldest request time & due count)
  const productOptions = useMemo(() => {
    const now = new Date(nowTick);
    const optionMap = new Map<
      string,
      {
        key: string;
        label: string;
        itemName: string;
        brandName: string | null;
        unitName: string | null;
        count: number;
        duplicateCount: number;
        dueCount: number;
        oldestRequestedAt: number;
        requestedAtSum: number;
        requestedAtCount: number;
        estimatedAmount: number;
      }
    >();

    for (const coupon of visibleRequested) {
      let key: string;
      let itemName: string;
      let unitName: string | null = null;
      // 브랜드는 populate 여부에 따라 없을 수 있어 항상 옵셔널 취급
      const brandName = getBrandName(coupon);

      if (
        coupon.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit._id
      ) {
        key = `unit:${coupon.selectedChoiceUnit._id}`;
        itemName =
          typeof coupon.giftItem === 'object'
            ? coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || ''
            : '';
        unitName = coupon.selectedChoiceUnit.name;
      } else if (typeof coupon.giftItem === 'object' && coupon.giftItem?._id) {
        key = `item:${coupon.giftItem._id}`;
        itemName = coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
      } else {
        continue;
      }

      // 상품명 - 브랜드 - 선택유닛명 (select 옵션 등 평문 표기용)
      const label = [itemName, brandName, unitName].filter(Boolean).join(' - ') || '-';

      const isDuplicate = !!(coupon as GiftCouponForRedemptionDashboard).isDuplicatePhone;
      const requestedAt = coupon.recipient?.requestedAt
        ? new Date(coupon.recipient.requestedAt).getTime()
        : Infinity;
      const hasValidTime = isFinite(requestedAt);
      const isDue = hasValidTime && isCouponSendDue(requestedAt, now);
      // 상품별 예상 금액 — 필터 키(item:)와 예측 키(info:)가 갈릴 수 있어 쿠폰 단위로 매핑
      const unitPrice = estimateUnitPriceByKey?.get(couponEstimateKey(coupon)) ?? 0;

      const existing = optionMap.get(key);
      if (existing) {
        existing.count++;
        if (isDuplicate) existing.duplicateCount++;
        if (isDue) existing.dueCount++;
        if (requestedAt < existing.oldestRequestedAt) {
          existing.oldestRequestedAt = requestedAt;
        }
        if (hasValidTime) {
          existing.requestedAtSum += requestedAt;
          existing.requestedAtCount++;
        }
        existing.estimatedAmount += unitPrice;
      } else {
        optionMap.set(key, {
          key,
          label,
          itemName,
          brandName,
          unitName,
          count: 1,
          duplicateCount: isDuplicate ? 1 : 0,
          dueCount: isDue ? 1 : 0,
          oldestRequestedAt: requestedAt,
          requestedAtSum: hasValidTime ? requestedAt : 0,
          requestedAtCount: hasValidTime ? 1 : 0,
          estimatedAmount: unitPrice,
        });
      }
    }

    return Array.from(optionMap.values())
      .map((v) => ({
        key: v.key,
        label: v.label,
        itemName: v.itemName,
        brandName: v.brandName,
        unitName: v.unitName,
        count: v.count,
        duplicateCount: v.duplicateCount,
        dueCount: v.dueCount,
        oldestRequestedAt: v.oldestRequestedAt,
        avgRequestedAt:
          v.requestedAtCount > 0
            ? Math.round(v.requestedAtSum / v.requestedAtCount)
            : Infinity,
        estimatedAmount: estimateUnitPriceByKey ? v.estimatedAmount : undefined,
      }))
      .sort((a, b) => a.oldestRequestedAt - b.oldestRequestedAt);
  }, [visibleRequested, nowTick, estimateUnitPriceByKey]);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = [...visibleRequested];

    if (productFilter !== 'all') {
      result = result.filter((item) => {
        if (productFilter.startsWith('unit:')) {
          const unitId = productFilter.slice(5);
          return (
            item.selectedChoiceUnit &&
            typeof item.selectedChoiceUnit === 'object' &&
            item.selectedChoiceUnit._id === unitId
          );
        } else if (productFilter.startsWith('item:')) {
          const itemId = productFilter.slice(5);
          return typeof item.giftItem === 'object' && item.giftItem?._id === itemId;
        }
        return true;
      });
    }

    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (
                item.giftItem?.name ||
                item.giftItem?.giftItemInfo?.name ||
                ''
              ).toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        const fullCode = (item.fullCode || '').toLowerCase();
        const order = typeof item.giftOrder === 'object' ? item.giftOrder : null;
        const ordererPhone = (order?.bugo?.member?.phoneNumber || '').replace(/-/g, '');
        return (
          phone.includes(search) ||
          itemName.includes(search) ||
          unitName.includes(search) ||
          fullCode.includes(search) ||
          ordererPhone.includes(search)
        );
      });
    }

    result.sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [visibleRequested, productFilter, searchText]);

  // Selection
  const isAllSelected = useMemo(
    () =>
      filteredData.length > 0 && filteredData.every((item) => selectedIds.has(item._id)),
    [filteredData, selectedIds],
  );

  // 초과발송(구매 수량 초과) 건 강제 발송 모드 — 관리자 시크릿 키로 잠금 해제.
  // 켜지면 단건/일괄 발송에서 초과발송 차단을 우회한다. (admin 손실 감수 예외 발송)
  const [allowOverIssue, setAllowOverIssue] = useState(false);

  const requestOverIssueOverride = useCallback(() => {
    if (allowOverIssue) return;
    const expected = process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '';
    const input = window.prompt(
      '초과발송(구매 수량 초과) 건을 강제로 발송하려면 관리자 시크릿 키를 입력하세요.\n(admin 손실이 발생할 수 있는 예외 발송입니다.)',
    );
    if (input == null) return; // 취소
    if (!expected || input.trim() !== expected) {
      alert('시크릿 키가 일치하지 않습니다.');
      return;
    }
    setAllowOverIssue(true);
  }, [allowOverIssue]);

  // 선택된 쿠폰 중 초과발송(위험) 건의 ID 목록 - 일괄발송 차단용
  const overIssuedSelectedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const coupon of visibleRequested) {
      if (selectedIds.has(coupon._id) && isOverIssued(coupon)) {
        ids.add(coupon._id);
      }
    }
    return ids;
  }, [visibleRequested, selectedIds]);

  // 선택분 충전 예상 — 전체 예측의 상품별 단가를 선택 쿠폰에 재적용 (백엔드 재호출 없음)
  const selectedChargeEstimate = useMemo(() => {
    if (!chargeEstimate || selectedIds.size === 0) return null;
    return computeSelectedChargeEstimate(
      chargeEstimate,
      visibleRequested.filter((c) => selectedIds.has(c._id)),
    );
  }, [chargeEstimate, selectedIds, visibleRequested]);

  // 선택 영역에서 초과발송 건만 일괄 해제
  const deselectOverIssued = useCallback(() => {
    if (overIssuedSelectedIds.size === 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      overIssuedSelectedIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [overIssuedSelectedIds]);

  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredData.map((item) => item._id)));
    }
  }, [isAllSelected, filteredData]);

  const handleToggleSelect = useCallback((couponId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  }, []);

  // Deliver modal
  const openDeliverModal = useCallback(
    (couponId: string) => {
      const target = visibleRequested.find((c) => c._id === couponId);
      if (!allowOverIssue && target && isOverIssued(target)) {
        alert(
          '구매 수량을 초과한 위험 쿠폰입니다.\n발송할 수 없습니다. 본사/관리자에 확인해주세요.',
        );
        return;
      }
      const priceRetail = getCouponSaleRetail(couponId, visibleRequested);
      setDeliverModal({
        couponIds: [couponId],
        priceRetail,
        priceWhole: '',
        priceRebate: '',
        deliveryChannel: '',
        amountConfirmed: false,
        isBulk: false,
      });
    },
    [visibleRequested, allowOverIssue],
  );

  const openBulkDeliverModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    if (!allowOverIssue && overIssuedSelectedIds.size > 0) {
      alert(
        `선택 항목에 초과발송 의심 건이 ${overIssuedSelectedIds.size}건 포함되어 있어 일괄 발송할 수 없습니다.\n해당 건들을 선택 해제한 뒤 다시 시도해주세요.`,
      );
      return;
    }
    const firstId = targetIds[0];
    if (!firstId) return;
    const priceRetail = getCouponSaleRetail(firstId, visibleRequested);
    setDeliverModal({
      couponIds: targetIds,
      priceRetail,
      priceWhole: '',
      priceRebate: '',
      deliveryChannel: '',
      amountConfirmed: false,
      isBulk: true,
    });
  }, [selectedIds, visibleRequested, overIssuedSelectedIds, allowOverIssue]);

  const handleConfirmDeliver = useCallback(async () => {
    if (!deliverModal) return;

    const priceWhole = Number(deliverModal.priceWhole);
    // 리베이트는 선택 입력 — 비우면 0으로 처리
    const priceRebate =
      deliverModal.priceRebate.trim() === '' ? 0 : Number(deliverModal.priceRebate);

    if (!deliverModal.deliveryChannel) {
      alert('발송 매체를 선택해주세요.\n매체를 선택하지 않으면 발송 처리할 수 없습니다.');
      return;
    }
    if (!deliverModal.priceWhole || isNaN(priceWhole)) {
      alert('도매가를 입력해주세요.');
      return;
    }
    if (isNaN(priceRebate)) {
      alert('리베이트는 숫자로 입력해주세요.');
      return;
    }
    if (!deliverModal.amountConfirmed) {
      const total = priceWhole * deliverModal.couponIds.length;
      alert(`총 발주비용(${total.toLocaleString()}원)이 맞는지 확인 후 체크해주세요.`);
      return;
    }

    const dto: DeliverCouponDto = {
      priceWhole,
      priceRetail: deliverModal.priceRetail,
      priceRebate,
      deliveryChannel: deliverModal.deliveryChannel,
    };

    if (deliverModal.isBulk) {
      setIsBulkProcessing(true);
      setDeliverModal(null);

      try {
        await deliverCoupons(deliverModal.couponIds, dto);
        alert(`${deliverModal.couponIds.length}건 발송 처리 완료`);
      } catch (error) {
        console.error('일괄 발송 처리 실패:', error);
        alert('일괄 발송 처리에 실패했습니다.');
      } finally {
        setIsBulkProcessing(false);
        setSelectedIds(new Set());
        invalidateDashboard();
      }
    } else {
      const couponId = deliverModal.couponIds[0] ?? '';
      setDeliveringId(couponId);
      setDeliverModal(null);

      try {
        await deliverCoupons([couponId], dto);
        invalidateDashboard();
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    }
  }, [deliverModal, invalidateDashboard]);

  const handleReject = useCallback(
    async (couponId: string) => {
      if (
        !confirm('이 쿠폰을 발송불가 처리하시겠습니까?\n(수신자 번호가 잘못된 경우 등)')
      ) {
        return;
      }
      try {
        await rejectCoupon(couponId);
        invalidateDashboard();
        alert('발송불가 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송불가 처리 실패:', error);
        alert('발송불가 처리에 실패했습니다.');
      }
    },
    [invalidateDashboard],
  );

  const handleUpdateRecipientPhone = useCallback(
    async (couponId: string, newPhone: string) => {
      const cleaned = newPhone.replace(/[^0-9]/g, '');
      if (cleaned.length < 10 || cleaned.length > 11) {
        alert('올바른 전화번호를 입력해주세요.');
        return false;
      }
      try {
        await updateRecipientPhone(couponId, cleaned);
        invalidateDashboard();
        showToast('수신자 번호가 변경되었습니다.');
        return true;
      } catch (error) {
        console.error('수신자 번호 변경 실패:', error);
        alert('수신자 번호 변경에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  const openHistoryModal = useCallback((coupon: GiftCouponForRedemption) => {
    const dc = coupon as GiftCouponForRedemptionDashboard;
    if (dc.phoneHistory && dc.phoneHistory.length > 0) {
      setHistoryModal({
        phone: coupon.recipient?.phoneNumber || '',
        history: dc.phoneHistory,
      });
    }
  }, []);

  const openShareHistoryModal = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.shareHistory && coupon.shareHistory.length > 0) {
      setShareHistoryModal({
        fullCode: coupon.fullCode || coupon._id,
        history: coupon.shareHistory,
      });
    }
  }, []);

  const handleBulkUpdateDeliveredCoupon = useCallback(
    async (couponIds: string[], dto: UpdateDeliveredCouponFieldsDto) => {
      if (couponIds.length === 0) return;
      if (dto.deliveryChannel === undefined && dto.priceWhole === undefined) return;

      const parts: string[] = [];
      if (dto.priceWhole !== undefined) {
        parts.push(`도매가: ${dto.priceWhole.toLocaleString()}원`);
      }
      if (dto.deliveryChannel !== undefined) {
        parts.push(`매체: ${dto.deliveryChannel || '입력안함'}`);
      }
      if (
        !confirm(`${couponIds.length}건을 다음과 같이 수정합니다.\n${parts.join('\n')}`)
      ) {
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const id of couponIds) {
        try {
          await updateDeliveredCouponFields(id, dto);
          successCount++;
        } catch (error) {
          console.error(`쿠폰 수정 실패 (${id}):`, error);
          failCount++;
        }
      }

      invalidateDashboard();

      if (failCount === 0) {
        showToast(`${successCount}건 수정 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    },
    [invalidateDashboard, showToast],
  );

  // 추가결제 대기 — (A) 추가결제 후 발송
  const handlePayExtraPayment = useCallback(
    async (couponId: string, dto: ExtraPaymentDto) => {
      try {
        await payExtraPayment(couponId, dto);
        invalidateDashboard();
        showToast('추가결제 처리 완료 — 발송대기로 이동했습니다.');
        return true;
      } catch (error) {
        console.error('추가결제 처리 실패:', error);
        alert(error instanceof Error ? error.message : '추가결제 처리에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  // 추가결제 대기 — (B) 기구매 미전달 쿠폰으로 대체발송
  const handleFulfillFromExisting = useCallback(
    async (couponId: string, donorCouponId: string) => {
      try {
        await fulfillFromExisting(couponId, donorCouponId);
        invalidateDashboard();
        showToast('대체발송 처리 완료 — 발송대기로 이동했습니다.');
        return true;
      } catch (error) {
        console.error('대체발송 처리 실패:', error);
        alert(error instanceof Error ? error.message : '대체발송 처리에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  // 추가결제 대기 — (C) 신청 취소 (스폰 쿠폰 hard-delete + 부수효과 되돌리기)
  const handleCancelSpawned = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 추가결제 대기 신청을 취소하시겠습니까?\n신청 쿠폰이 삭제되고 원본 쿠폰의 추가교환 허용 슬롯이 복구됩니다.\n(실제 결제된 건은 취소할 수 없으며 환불로 처리해야 합니다.)',
        )
      ) {
        return false;
      }
      try {
        await cancelSpawnedCoupon(couponId);
        invalidateDashboard();
        showToast('신청이 취소되었습니다.');
        return true;
      } catch (error) {
        console.error('신청 취소 실패:', error);
        alert(error instanceof Error ? error.message : '신청 취소에 실패했습니다.');
        return false;
      }
    },
    [invalidateDashboard, showToast],
  );

  return {
    // Data
    dashboardStatus,
    statistics,
    visibleRequested,
    extraPaymentPending,
    todayDelivered,
    filteredData,
    productOptions,
    chargeEstimate,
    selectedChargeEstimate,
    channelBalances,

    // Filters
    productFilter,
    setProductFilter,
    searchText,
    setSearchText,

    // Selection
    selectedIds,
    isAllSelected,
    handleToggleAll,
    handleToggleSelect,
    setSelectedIds,
    overIssuedSelectedIds,
    deselectOverIssued,
    allowOverIssue,
    requestOverIssueOverride,

    // Deliver
    deliveringId,
    isBulkProcessing,
    openDeliverModal,
    openBulkDeliverModal,

    // Modals
    deliverModal,
    setDeliverModal,
    handleConfirmDeliver,
    handleReject,
    handleUpdateRecipientPhone,
    historyModal,
    setHistoryModal,
    openHistoryModal,
    shareHistoryModal,
    setShareHistoryModal,
    openShareHistoryModal,

    // Today delivered
    showTodayDelivered,
    setShowTodayDelivered,

    // Bulk update delivered coupon (도매가/매체)
    handleBulkUpdateDeliveredCoupon,

    // 추가결제 대기 처리 (A: 추가결제 / B: 대체발송 / C: 신청취소)
    handlePayExtraPayment,
    handleFulfillFromExisting,
    handleCancelSpawned,
    getDonorCandidates,

    // Toast & clipboard
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,
  };
}
